import type {
	AppliedFilterPill,
	FacetMultiSelectFilterValue,
	FacetSingleSelectFilterValue,
	FilterConfig,
	FilterValue,
	PricingFilterValue,
	SearchQueryParams,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { FacetMultiSelectFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/facet-multi-select-filter-strategy';
import { FacetSingleSelectFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/facet-single-select-filter-strategy';
import { PricingFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/pricing-filter-strategy';

interface FilterStrategyOptions {
	categoryCode: string;
}

export interface FacetFilterValue {
	facetId: string;
	facetName: string;
	facetValue: string;
}

export interface AppliedMultiSelectFilterValue {
	value: FacetFilterValue[];
	config: FilterConfig;
}

export interface AppliedSingleSelectFilterValue {
	value: FacetFilterValue;
	config: FilterConfig;
}

export interface AppliedPricingFilterValue {
	value: { minPrice?: number; maxPrice?: number; vanitySlug?: string };
	config: FilterConfig;
}

export type AppliedFilterValue =
	| AppliedMultiSelectFilterValue
	| AppliedSingleSelectFilterValue
	| AppliedPricingFilterValue;

export interface FilterStrategy {
	type: string;

	/**
	 * Calculates the count of applied filters for this strategy.
	 * This is used to display the number of active filters in the UI.
	 * @return {number} The count of applied filters.
	 */
	calculateCount(): number;

	/**
	 * Retrieves the data associated with this filter strategy.
	 * @return {AppliedFilterValue} The data of the filter strategy.
	 */
	getData(): AppliedFilterValue;

	/**
	 * Generates the applied filter pills based on the provided filter configurations.
	 * This is used to display the applied filter pills in the UI
	 * @return {AppliedFilterPill[]} An array of applied filter pills.
	 */
	getAppliedFilterPills(dynamicMaxRange?: number): AppliedFilterPill[];

	/**
	 * Retrieves the facet ids of the applied facets.
	 * @return {string[]} An array of applied facet IDs.
	 */
	/**
	 * Returns facet IDs for the applied filter
	 * Used for analytics tracking and UI state management
	 */
	getAppliedFacetIds(): string[];

	/**
	 * Returns facet IDs specifically for SEO text matching via seoFilter()
	 * For most filters this is the same as getAppliedFacetIds(), but pricing
	 * filters return vanity slug facet IDs here for SEO title generation
	 */
	getSeoFacetIds(): string[];
	getSearchQueryParams(dynamicMaxRange?: number): Partial<SearchQueryParams>;

	/**
	 * Retrieves the facet names of the applied facets.
	 * @return {string[]} An array of applied facet names.
	 */
	getAppliedFacetNames(): string[];

	/**
	 * Retrieves the facet values of the applied facets.
	 * @return {string[]} An array of applied facet values.
	 */
	getAppliedFacetValues(): string[];

	/**
	 * Retrieves the url query params for this filter strategy.
	 * @return {Record<string, string>} An object representing the url query params.
	 */
	getUrlQueryParams(dynamicMaxRange?: number): { key: string; value: string }[];

	/**
	 * Retrieves the applied filters formatting for tracking purposes.
	 * This is used to send the applied filters information to analytics platforms.
	 * @return {Record<string, string | string[]>} An object representing the applied filters for tracking.
	 */
	getAppliedFiltersForTracking(): { [filterName: string]: string | string[] };
}

export function createFilterStrategy(
	filterValue: FilterValue,
	config: FilterConfig,
	options: FilterStrategyOptions,
) {
	let newFilterStrategy: FilterStrategy;
	try {
		const { type } = config;
		switch (type) {
			case 'facet-multi-select':
				newFilterStrategy = new FacetMultiSelectFilterStrategy(
					filterValue as unknown as FacetMultiSelectFilterValue,
					config,
				);
				break;
			case 'facet-single-select':
				newFilterStrategy = new FacetSingleSelectFilterStrategy(
					filterValue as unknown as FacetSingleSelectFilterValue,
					config,
				);
				break;
			case 'pricing':
				newFilterStrategy = new PricingFilterStrategy(
					filterValue as unknown as PricingFilterValue,
					config,
					options.categoryCode,
				);
				break;
		}
	} catch {
		return null;
	}

	return newFilterStrategy;
}
